import { Stack } from '@mui/material';
import LangEnum from '../../../../../lang/LangEnum';
import { UsedInOfflineMode } from '../../../../offline/types/UsedInOfflineMode';
import { ProductMedia } from '../../../ProductTypes';
import ProductDetailZoom from '../../../details/gallery/ProductDetailZoom';
import ProductDetailsGallery from '../../../details/gallery/ProductDetailsGallery';
import ProductDetailsHeader from '../../ProductDetailsHeader';
import { ProductDetailsListWidthProvider } from '../../contexts/ProductDetailsListWidth.context';
import useProductDetailsContent from '../../hooks/useProductDetailsContent';
import { ProductLockProvider } from '../../hooks/useProductLock';
import CollapsibleSection from '../CollapsibleSection/CollapsibleSection';
import ProductDetailsEntityList from '../ProductDetailsEntityList';
import ProductDetailsInfoSections from '../ProductDetailsInfoSections';
import ProductDetailsMassActionBar from '../ProductDetailsMassActionBar/ProductDetailsMassActionBar';
import ProductDetailsScrollHeader from '../ProductDetailsScrollHeader/ProductDetailsScrollHeader';
import ConfirmPasteGridMassActionModal from '../confirmPasteGridMassActionModal/ConfirmPasteGridMassActionModal';
import ProductFilters from '../filters/ProductFilters/ProductFilters';
import { useProductDetailsContentStyles } from './productDetailsContent.styles';

const ProductDetailsContent = ({ isOfflineMode = false }: UsedInOfflineMode) => {
  const {
    entity,
    zoomIn,
    zoomOut,
    zoomMedia,
    isLoading,
    galleryMedias,
    isLoadingMedias,
    setMediaElement,
    setListElement,
    listWidth,
    scrollElement,
    setScrollElement,
    copiedGrid,
    totalItems,
    mediaWidth,
    mediaHeight,
    galleryColumnWidth,
    setPanelElement,
    isStackedLayout,
    mappedProps,
    disableGrid,
    canPasteGrid,
    totalSelected,
    batchPasteGrid,
    isGridOpen,
    toggleGrid,
    handleCloseModal,
    filtersRowRef,
    isSectionOpen,
    toggleSection,
    isMobileHeaderFixed,
    titleElement,
    setTitleElement,
    allProductColorIds,
    pasteGridMassAction,
    suggestedProducts,
    focusZoomPercentToScroll,
    openPasteGridConfirmModal,
    closePasteGridConfirmModal,
    isPasteGridConfirmModalOpen,
    massActionBlockReasonMessage,
    isLoadingPasteGridMassAction,
    toggleSelectAllItemsMassAction,
    deselectAllItemsMassAction,
  } = useProductDetailsContent({ isOfflineMode });

  const { classes } = useProductDetailsContentStyles({ galleryColumnWidth });

  const gallery = (
    <div ref={setMediaElement} className={classes.media}>
      <ProductDetailsGallery
        galleryWidth={mediaWidth}
        galleryHeight={mediaHeight}
        isLoading={isLoading || isLoadingMedias}
        medias={galleryMedias}
        onZoom={zoomIn}
      />
    </div>
  );

  return (
    <ProductDetailsListWidthProvider width={listWidth}>
      <div ref={setPanelElement} className={classes.root}>
        {!isStackedLayout && gallery}

        <div ref={setScrollElement} className={classes.content}>
          {mappedProps && !copiedGrid.product && (
            <ProductDetailsScrollHeader
              name={mappedProps.name}
              code={mappedProps.code}
              referenceCode={mappedProps.referenceCode}
              titleElement={titleElement}
              scrollElement={scrollElement}
              onClose={handleCloseModal}
            />
          )}

          {!!copiedGrid.product && (
            <div className={classes.massActionContainer}>
              <ProductDetailsMassActionBar
                totalItems={totalItems}
                totalSelected={totalSelected}
                isAllItemsSelected={pasteGridMassAction.isAllItemsSelected}
                isPasteDisabled={!canPasteGrid}
                blockReasonMessage={massActionBlockReasonMessage}
                onClickPaste={openPasteGridConfirmModal}
                toggleSelectAllItems={toggleSelectAllItemsMassAction}
                deselectAllItems={deselectAllItemsMassAction}
              />
            </div>
          )}

          {isStackedLayout && gallery}

          {mappedProps && (
            <>
              <ProductLockProvider>
                <div className={classes.listSection}>
                  <ProductDetailsHeader
                    name={mappedProps.name}
                    code={mappedProps.code}
                    tags={mappedProps.tags}
                    prices={mappedProps.prices}
                    allColorIds={allProductColorIds}
                    referenceCode={mappedProps.referenceCode}
                    productColorName={mappedProps.productColorName}
                    productColorCode={mappedProps.productColorCode}
                    isMobileHeaderFixed={isMobileHeaderFixed}
                    titleRef={setTitleElement}
                  />
                </div>

                <CollapsibleSection
                  title={LangEnum.GLOBAL_GRIDS}
                  scrollElement={scrollElement}
                  isOpen={isGridOpen}
                  onToggle={toggleGrid}
                  openHeaderContent={
                    <Stack
                      gap={1}
                      ref={filtersRowRef}
                      flex={1}
                      minWidth={0}
                      direction="row"
                      alignItems="center"
                    >
                      <ProductFilters
                        productColorId={mappedProps.productColorId}
                        allColorIds={allProductColorIds}
                      />
                    </Stack>
                  }
                >
                  <div ref={setListElement} className={classes.list}>
                    <ProductDetailsEntityList
                      entity={entity}
                      disableGrid={disableGrid}
                      scrollParent={scrollElement}
                      isOfflineMode={isOfflineMode}
                      suggestedProducts={suggestedProducts}
                    />
                  </div>
                </CollapsibleSection>
              </ProductLockProvider>

              <ProductDetailsInfoSections
                resume={mappedProps.resume}
                productId={mappedProps.productId}
                attributes={mappedProps.attributes}
                description={mappedProps.description}
                isOfflineMode={isOfflineMode}
                scrollElement={scrollElement}
                productColorId={mappedProps.productColorId}
                sections={{ isSectionOpen, toggleSection }}
              />
            </>
          )}

          <ProductDetailZoom<ProductMedia>
            open={!!zoomMedia}
            title={mappedProps?.name}
            onClose={zoomOut}
            selectedMedia={zoomMedia}
            medias={galleryMedias.filter((media) => media.type === 'IMAGE')}
            focusPercentToScroll={focusZoomPercentToScroll}
          />

          <ConfirmPasteGridMassActionModal
            isLoading={isLoadingPasteGridMassAction}
            isOpen={isPasteGridConfirmModalOpen}
            onClose={closePasteGridConfirmModal}
            onConfirm={batchPasteGrid}
          />
        </div>
      </div>
    </ProductDetailsListWidthProvider>
  );
};

export default ProductDetailsContent;
